<template>
  <v-layout :id="id" style="height: 94%" />
</template>

<script>
import OpenSeadragon from 'openseadragon'
import { Overlay } from './../../helpers/PaperjsOverlay'
import { Path, Point, Raster } from 'paper/dist/paper-core'
const paper = require('paper')

export default {
  name: 'OpenSeaDragonViewer',
  props: {
    id: {
      type: String,
      default: 'openseadragon-viewer'
    },
    shouldShowPois: {
      type: Boolean
    },
    options: {
      type: Object
    },
    i18n: {
      type: Object
    },
    currentPage: {
      type: Number,
      default: 0
    },
    messages: {
      type: Object,
      default: () => ({
        'Tooltips.FullPage': '',
        'Tooltips.Home': '',
        'Tooltips.ZoomIn': '',
        'Tooltips.ZoomOut': '',
        'Tooltips.NextPage': '',
        'Tooltips.PreviousPage': '',
        'Tooltips.RotateLeft': '',
        'Tooltips.RotateRight': '',
        'Errors.Dzc': '',
        'Errors.Dzi': '',
        'Errors.Xml': '',
        'Errors.ImageFormat': '',
        'Errors.Security': '',
        'Errors.Status': '',
        'Errors.OpenFailed': ''
      })
    },
    tileSources: {
      type: Array
    },
    polygonsData: {
      type: Array
    },
    // Persisted POIs already saved on this media item (loaded fresh whenever
    // the "Edit links for pois" section opens) — the markers to draw.
    poisData: {
      type: Array,
      default: () => []
    },
    affectAllPolygons: {
      type: Boolean,
      default: false
    },
    translation: {
      type: Object,
      default: () => ({ x: 0, y: 0 })
    },
    scaleValue: {
      type: Number,
      default: 1
    }
  },
  data: function() {
    return {
      viewer: null,
      debugMessage: '',
      componentKey: 0,
      overlay: null,
      path: null,
      tool: null,
      scope: null,
      segment: null,
      movePath: false,
      lastSelected: null,
      dragging: false,
      values: {
        paths: 1,
        minPoints: 4,
        maxPoints: 4
      },
      hitOptions: {
        segments: true,
        stroke: true,
        fill: true,
        tolerance: 5
      },
      drawMarkers: false,
      ourPaper: null,
      lastClickPosition: null,
      currentlyAddedPoi: null,
      previouslyAddedPois: [],
      previouslyAddedPoiRasters: [],
      previouslyAddedPoiTexts: [],
      // Set while an actual poi drag is in progress — dragEndHandler doubles
      // as the release handler (fires on every mouseup, drag or not), so
      // without this a plain click-to-select would also emit poi-moved.
      poiDragInProgress: false,
      originalPolygonsData: [] // To store the initial coordinates and sizes of all polygons
    }
  },
  watch: {
    polygonsData: function() {
      this.importFromJSON(this.polygonsData)
    },
    // Redraw whenever the parent's poi list changes (add/delete). Calling
    // insertMarkersForPois() imperatively via $refs right after the parent
    // updates its data is unreliable — Vue propagates prop changes to this
    // component asynchronously, so an immediate imperative call can read the
    // still-stale prop value; since insertMarkersForPois() always clears
    // existing markers first, that made every poi vanish. Reacting to the
    // prop change directly guarantees this runs against the new value.
    poisData: function() {
      this.insertMarkersForPois()
    },
    currentPage: function(newVal, oldValue) {
      if (this.viewer !== null) {
        this.viewer.goToPage(newVal)
      }
    },
    tileSources: function(newVal, oldValue) {
      if (this.viewer !== null) {
        this.viewer.open(newVal)
      }
    },
    // Watch for changes in translation or scaleValue and update accordingly
    translation: {
      handler() {
        if (this.affectAllPolygons) {
          this.moveAllPaths() // Update movement on translation change
        }
      },
      deep: true
    },
    scaleValue(newValue) {
      this.scaleAllPaths() // Update scaling on scaleValue change
    }
  },
  created() {},
  mounted() {
    this.setup()
    // this.insertMarkersForPois()
    paper.settings.handleSize = 16
  },
  updated() {
    this.setup()
    // this.insertMarkersForPois()
  },
  beforeDestroy: function() {
    this.viewer.destroy()
    this.viewer = null
  },
  methods: {
    insertMarkersForPois: function() {
      // Clear markers from any previous call — setup() (which calls this) runs
      // on every mounted/updated, and this is also invoked directly whenever
      // the "Edit links for pois" section opens, so without clearing first,
      // repeated calls would stack duplicate markers/labels.
      if (Array.isArray(this.previouslyAddedPoiRasters)) {
        this.previouslyAddedPoiRasters.forEach(raster => raster.remove())
      }
      if (Array.isArray(this.previouslyAddedPoiTexts)) {
        this.previouslyAddedPoiTexts.forEach(text => text.remove())
      }
      this.previouslyAddedPoiRasters = []
      this.previouslyAddedPoiTexts = []
      this.previouslyAddedPois = []

      // The persisted pois for this media item, passed in via the poisData
      // prop — NOT MediaStore.linkedPois, which is only a transient,
      // in-session list of pois added during the current visit (and was also
      // read one level too deep here, `linkedPois[0]`, a single poi object
      // rather than the array, so this never actually rendered anything).
      const pois = Array.isArray(this.poisData) ? this.poisData : []
      if (!pois.length) return

      var src = require('../../assets/map-marker-filled.png')
      pois.forEach(e => {
        let point = new Point(e.poiCoordinates[0], e.poiCoordinates[1])
        let raster = new paper.Raster({
          source: src,
          position: point,
          // id is carried through so a click-to-select can be matched back to
          // its persisted poi entry for deletion.
          data: { id: e.id, name: e.name, type: e.type }
        })
        var text = new paper.PointText({
          point: [e.poiCoordinates[0], e.poiCoordinates[1] + 70],
          justification: 'center',
          fillColor: 'black',
          content: e.name,
          fontWeight: 'bold',
          fontSize: 40,
          strokeColor: new paper.Color(1, 1, 1),
          strokeWidth: 2,
          // Tagged as type 'poi' too so removeImagesFromProject's existing
          // filter (by data.type) strips labels along with their markers
          // before the polygon JSON is exported on save.
          data: { id: e.id, type: 'poi', role: 'label' }
        })
        // Excluded from hit-testing (Paper.js's _hitTest skips locked items)
        // so the label can never intercept a click meant for its marker — a
        // click landing on the text was hitting this item instead of the
        // raster, taking a code path that assumes a linkedRecord (which poi
        // items don't have) and silently throwing, which is why clicking a
        // poi often didn't seem to do anything.
        text.locked = true

        this.previouslyAddedPoiRasters.push(raster)
        this.previouslyAddedPoiTexts.push(text)
        this.previouslyAddedPois.push(e)
        setTimeout(() => {
          raster.scale(
            (3.5 * this.calculateResizeFactor()) /
              this.viewer.viewport.getZoom()
          )
        }, 100)
      })
    },
    // Deletes the currently selected POI marker (raster + its paired text
    // label) from the canvas. Mirrors deleteSelectedPolygon(), but also
    // removes the label — a separate PointText item deleteSelectedPolygon()'s
    // generic remove() has no knowledge of. Returns the deleted poi's data
    // ({id, name, type}) so the caller can drop the matching persisted entry,
    // or null if nothing was selected.
    deleteSelectedPoi() {
      if (!this.lastSelected) return null
      const raster = this.lastSelected
      const deletedData = raster.data
      const idx = this.previouslyAddedPoiRasters.indexOf(raster)
      if (idx !== -1) {
        const text = this.previouslyAddedPoiTexts[idx]
        if (text) text.remove()
        this.previouslyAddedPoiRasters.splice(idx, 1)
        this.previouslyAddedPoiTexts.splice(idx, 1)
        this.previouslyAddedPois.splice(idx, 1)
      }
      raster.remove()
      this.lastSelected = null
      this.onDelete()
      return deletedData
    },
    removeImagesFromProject() {
      var items = paper.project.activeLayer.getItems()
      items = items.filter(e => e.data.type === 'poi')
      items.map(e => e.remove())
      let result = this.exportToJSON()
      return result
    },
    setup: function() {
      if (!this.viewer) {
        var self = this
        const _options = Object.assign({}, this.options)
        _options.id = this.id
        this.viewer = OpenSeadragon(_options)

        //This is a ipad performance fix
        // this.viewer.addHandler("canvas-release", function(viewer) {
        //   document.getElementById("favorites").focus();
        // });
        // this.viewer.addHandler("canvas-pinch", function(viewer) {
        //   document.getElementById("favorites").focus();
        // });
        // this.viewer.addHandler("animation-finish", function(viewer) {
        //   document.getElementById("favorites").focus();
        // });
      }

      this.viewer.open(this.tileSources)
      this.viewer.gestureSettingsMouse.clickToZoom = false
      this.paperOverlayInit(this.viewer)

      // event listeners

      // this.overlay
      // 	.paperCanvas()
      // 	.addEventListener("mousedown", self.onMouseDown);
      // this.overlay.paperCanvas().addEventListener("mouseup", self.onMouseUp);
      // this.overlay
      // 	.paperCanvas()
      // 	.addEventListener("mousemove", self.onMouseMove);
      // this.overlay.paperCanvas().addEventListener("keypress", self.onKeyUp);

      new OpenSeadragon.MouseTracker({
        element: self.viewer.canvas,
        pressHandler: self.onMouseDown,
        dragHandler: self.doDrag,
        dragEndHandler: self.dragEndHandler,
        keyUpHandler: self.onKeyUp,
        releaseHandler: self.dragEndHandler
      }).setTracking(true)

      this.importFromJSON(this.polygonsData)
      if (this.shouldShowPois) {
        this.insertMarkersForPois()
      }
      this.viewer.addHandler('zoom', function(viewer, contentSize) {
        let currentZoom = self.viewer.viewport.getZoom()

        if (
          currentZoom + 0.1 < self.viewer.maxZoomLevel &&
          currentZoom - 0.1 > self.viewer.minZoomLevel
        ) {
          var src = require('../../assets/map-marker-filled.png')

          if (self.lastClickPosition !== null) {
            if (self.currentlyAddedPoi !== null) self.currentlyAddedPoi.remove()
            let point = new Point(
              self.lastClickPosition.x,
              self.lastClickPosition.y
            )

            self.currentlyAddedPoi = new paper.Raster({
              source: src,
              position: point,
              data: { name: 'currentPin', type: 'poiType' }
            })
          }
          if (self.currentlyAddedPoi != null) {
            self.currentlyAddedPoi.scale(
              (3.5 * self.calculateResizeFactor()) / currentZoom
            )
          }
          if (Array.isArray(self.previouslyAddedPoiRasters)) {
            console.log(self.previouslyAddedPoiRasters)
            self.previouslyAddedPoiRasters.forEach(raster => {
              raster.clear()
            })
            self.previouslyAddedPoiRasters = []
          }

          if (Array.isArray(self.previouslyAddedPois)) {
            self.previouslyAddedPois.forEach(poi => {
              let point = new Point(
                poi.poiCoordinates[0],
                poi.poiCoordinates[1]
              )
              var raster = new paper.Raster({
                source: src,
                position: point,
                data: { name: poi.name, type: poi.type }
              })
              raster.scale((3.5 * self.calculateResizeFactor()) / currentZoom)
              self.previouslyAddedPoiRasters.push(raster)
            })
          }
        }
      })
      this.storeInitialPolygonsData()
      //window.addEventListener("keypress", self.onKeyUp);
    },
    paperOverlayInit(viewer) {
      var self = this
      //paper = new paper.PaperScope();
      this.overlay = new Overlay(viewer)

      // paper.setup(this.overlay.paperCanvas());
      // this.tool = this.createTool(self.scope);

      OpenSeadragon.Viewer.prototype.paperjsOverlay = this.overlay

      console.log('OVERLAYING paper')
      // this._paperjsOverlayInfo = new Overlay(this);
      // return this._paperjsOverlayInfo;

      this.overlay.resize()
      this.overlay.resizecanvas()

      // paper = new paper.PaperScope();
      // paper.setup(this.overlay.paperCanvas());
      // this.tool = this.createTool(self.scope);
      //console.log("SCOPE: ", paper);
      //paper.view.draw();

      // new OpenSeadragon.MouseTracker({
      //   element: self.viewer.canvas,
      //   pressHandler: self.press_handler,
      //   dragHandler: self.drag_handler,
      //   dragEndHandler: self.dragEnd_handler
      // }).setTracking(true);

      //this.overlay.paperCanvas().add(circle);
      //paper.project.activeLayer.addChild(circle);
    },
    onMouseDown(event) {
      // in order to access functions in nested tool

      let self = this

      // create drawing tool
      //this.tool = this.createTool(paper);
      // this.tool.onMouseDown = (event) => {
      // 	// init path
      // 	self.path = self.pathCreate(self.scope);
      // 	// add point to path
      // 	self.path.add(event.point);
      // };
      // this.tool.onMouseDrag = (event) => {
      // 	self.path.add(event);
      // };
      // this.tool.onMouseUp = (event) => {
      // 	// line completed
      // 	self.path.add(event.point);
      // };
      let markerResizeFactor = this.calculateResizeFactor()

      // Hit-test BEFORE placing the "new poi goes here" preview pin below —
      // the preview pin used to be created unconditionally, first, right at
      // the click position. Since it's a brand-new item inserted on top of
      // whatever's already there, every click — including clicks squarely on
      // an EXISTING marker — then hit-tested against that fresh ghost pin
      // instead of the real marker beneath it. That's why clicking an
      // existing poi never selected it, and why an unmarked pin appeared to
      // "jump around" under the cursor on every click.
      self.segment = self.path = null
      var transformed_point = paper.view.viewToProject(
        new paper.Point(event.position.x, event.position.y)
      )
      self.$emit('point', transformed_point)
      var hitResult = paper.project.hitTest(transformed_point, self.hitOptions)

      const clickedExistingPoi =
        hitResult &&
        hitResult.type === 'pixel' &&
        hitResult.item.data &&
        hitResult.item.data.type === 'poi'

      // Only preview a new marker at the click position when the click isn't
      // selecting an existing one.
      if (this.shouldShowPois && !clickedExistingPoi) {
        this.lastClickPosition = paper.view.viewToProject(
          new paper.Point(event.position.x, event.position.y)
        )
        var src = require('../../assets/map-marker-filled.png')

        if (this.lastClickPosition !== null) {
          if (this.currentlyAddedPoi !== null) this.currentlyAddedPoi.remove()
          let point = new Point(
            this.lastClickPosition.x,
            this.lastClickPosition.y
          )

          this.currentlyAddedPoi = new paper.Raster({
            source: src,
            position: point,
            data: { name: 'currentPin', type: 'poiType' }
          })
          this.currentlyAddedPoi.scale(
            (3.5 * this.calculateResizeFactor()) /
              this.viewer.viewport.getZoom()
          )
          this.currentlyAddedPoi.handles.remove() // this will throw Uncaught errors but it works.
        }

        // if we zoom in / zoom out the pin gets redrawn in the addHandler ( 'zoom' ) function
      }

      // remove selection
      if (self.lastSelected && self.lastSelected !== event.item) {
        self.lastSelected.selected = false
        self.onDeselect()
      }
      if (!hitResult) {
        this.onDeselect()
        return
      }
      if (event.originalEvent.shiftKey) {
        if (hitResult.type == 'segment') {
          hitResult.segment.remove()
        }
      }

      if (hitResult.type === 'pixel') {
        hitResult.item.selected = true
        if (self.lastSelected) {
          self.lastSelected.selected = false
        }
        self.lastSelected = hitResult.item
        // Rasters (poi markers) only ever hit-test as 'pixel', which never
        // set self.path — so doDrag()'s existing "move self.path" logic never
        // ran for them and dragging a selected marker did nothing.
        self.path = hitResult.item
        self.onSelect(hitResult.item)
      } else {
        this.viewer.setMouseNavEnabled(false)
        // Only polygon-type hits carry a linkedRecord (poi markers/labels
        // don't) — guard instead of assuming it exists, so a hit that lands
        // here for any other reason can't throw and silently abort the whole
        // click (which was swallowing every click that landed on a poi's text
        // label rather than its marker image).
        const linkedRecord =
          hitResult.item.data && hitResult.item.data.linkedRecord
        if (linkedRecord) {
          const notLinkedAssets = this.$store.state.MediaStore.notLinkedAssets
          const asset = notLinkedAssets.filter(
            ele => ele.id === linkedRecord.id
          )
          if (asset.length) {
            hitResult.item.data.name = asset[0].name
          }
        }

        hitResult.item.selected = true
        self.lastSelected = hitResult.item
        self.onSelect(self.lastSelected)
        self.path = hitResult.item
        if (hitResult.type == 'segment') {
          if (event.originalEvent.shiftKey) {
            hitResult.segment.remove()
            return
          }
          self.segment = hitResult.segment
        } else if (hitResult.type == 'stroke') {
          var location = hitResult.location
          self.segment = self.path.insert(location.index + 1, transformed_point)
          //self.path.smooth();
        }
      }
      if (hitResult.type == 'fill') {
        self.movePath = true
      } else {
        self.movePath = false
      }
      if (self.movePath) paper.project.activeLayer.addChild(hitResult.item)
    },
    doDrag(event) {
      let self = this
      var transformed_point1 = paper.view.viewToProject(new paper.Point(0, 0))
      var transformed_point2 = paper.view.viewToProject(
        new paper.Point(event.delta.x, event.delta.y)
      )
      if (self.segment) {
        //self.segment.point += event.delta;
        // self.segment.point = new paper.Point(
        // 	self.segment.point.x + event.delta.x,
        // 	self.segment.point.y + event.delta.y
        // );

        self.segment.point = self.segment.point.add(
          transformed_point2.subtract(transformed_point1)
        )
        this.viewer.setMouseNavEnabled(false)

        paper.view.draw()
        //path.smooth();
      } else if (self.path) {
        //self.path.position += event.delta;
        // self.path.position = new paper.Point(
        // 	self.path.position.x + event.delta.x,
        // 	self.path.position.y + event.delta.y

        const delta = transformed_point2.subtract(transformed_point1)
        self.path.position = self.path.position.add(delta)

        // A poi marker's label is a separate item, not a child of the
        // raster, so it has to be moved along with it explicitly, and its
        // persisted coordinates kept in step so the drag actually sticks
        // (see dragEndHandler, which reads previouslyAddedPois to persist it).
        if (self.path.data && self.path.data.type === 'poi') {
          self.poiDragInProgress = true
          const idx = self.previouslyAddedPoiRasters.indexOf(self.path)
          if (idx !== -1) {
            const text = self.previouslyAddedPoiTexts[idx]
            if (text) text.position = text.position.add(delta)
            const poi = self.previouslyAddedPois[idx]
            if (poi) {
              poi.poiCoordinates = [self.path.position.x, self.path.position.y]
            }
          }
        }

        this.viewer.setMouseNavEnabled(false)

        paper.view.draw()
      }
    },

    onKeyUp(event) {
      let self = this

      // When a key is released, set the content of the text item:
      //text.content = 'The ' + event.key + ' key was released!';
      if (event.originalEvent.key === 'c') {
        self.createPaths()
      }

      if (event.originalEvent.key === 'd') {
        self.deleteSelectedPolygon()
      }
    },

    createPaths() {
      const size = this.calculateNewPolygonSize()
      var pointsDelta = this.values.maxPoints - this.values.minPoints
      for (var i = 0; i < this.values.paths; i++) {
        var points =
          this.values.minPoints + Math.floor(Math.random() * pointsDelta)

        this.path = this.createBlob(paper.view.center, size, points)
        this.path.fillColor = {
          hue: 0,
          saturation: 1,
          lightness: 1,
          alpha: 0.5
        }
        this.path.strokeColor = 'white'
        this.path.strokeWidth = 3
      }
    },
    // A new polygon should be a sensible fraction of the actual image it's
    // being drawn on, not a fixed pixel size — Paper.js's project space here
    // IS the DZI image's own native pixel space (see PaperjsOverlay.js's
    // viewportToImageZoom/viewportToImageCoordinates mapping), so a fixed
    // 400 made new shapes cover almost the whole screen on a small image and
    // be barely visible on a large one. Sized to a fifth of the image's
    // shorter side, so it's a reasonable, visible starting shape regardless
    // of resolution.
    calculateNewPolygonSize() {
      const fallback = 400
      if (
        !this.viewer ||
        !this.viewer.world ||
        !this.viewer.world.getItemAt(0)
      ) {
        return fallback
      }
      const imageSize = this.viewer.world.getItemAt(0).getContentSize()
      if (!imageSize || !imageSize.x || !imageSize.y) return fallback
      return Math.min(imageSize.x, imageSize.y) / 5
    },

    createBlob(center, size, points) {
      // center is the shape's centre point, not its corner — offset by half
      // the size so the new polygon is actually centred where the user is
      // looking, instead of starting there and extending down-right.
      const topLeft = new paper.Point(center.x - size / 2, center.y - size / 2)
      var rectangle = new paper.Rectangle(topLeft, new paper.Size(size, size))
      var path = new paper.Path.Rectangle(rectangle)
      path.closed = true
      path.data = Object.assign(path.data, { linkedRecord: {} })

      return path
    },

    changeZoom(oldZoom, delta) {
      var factor = 1.05
      if (delta < 0) {
        return oldZoom * factor
      }

      if (delta > 0) {
        return oldZoom / factor
      }
    },

    changeCenter(oldCenter, deltaX, deltaY, factor) {
      var offset = new paper.Point(deltaX, -deltaY)
      offset = offset.multiply(factor)
      oldCenter.add(offset)
    },

    dragEndHandler(event) {
      this.viewer.setMouseNavEnabled(true)
      // Polygons persist wherever they're dragged to automatically, because
      // Save fully re-exports the canvas into JSON. Poi entries are separate
      // data objects that only get created/removed explicitly, so an actual
      // drag has to be pushed up to the parent as its own event or the
      // marker snaps back to its old position next time markers are redrawn.
      // This handler also doubles as the release handler (fires on every
      // mouseup, drag or not), so poiDragInProgress guards against emitting
      // on a plain click-to-select where nothing actually moved.
      if (this.poiDragInProgress && this.path && this.path.data) {
        this.$emit('poi-moved', {
          id: this.path.data.id,
          poiCoordinates: [
            Math.round(this.path.position.x),
            Math.round(this.path.position.y)
          ]
        })
      }
      this.poiDragInProgress = false
      //this.hitResult = null;
    },
    importFromJSON(data) {
      if (data) {
        paper.project.clear()
        paper.project.activeLayer.selected = false
        paper.project.importJSON(data)
      }
    },
    exportToJSON() {
      paper.project.activeLayer.selected = false
      return paper.project.exportJSON({ asString: false })
    },

    getSelectedPolygon() {
      return this.lastSelected
    },
    // Null-safe delete of the currently selected polygon; used by both the
    // 'd' shortcut and the toolbar "Delete polygon" button.
    deleteSelectedPolygon() {
      if (!this.lastSelected) return
      this.lastSelected.selected = false
      this.lastSelected.remove()
      this.lastSelected = null
      this.onDelete()
    },

    onSelect(path) {
      this.$emit('selected', path)
    },

    onDeselect() {
      var children = paper.project.activeLayer.getChildren()
      children.forEach(child => (child._selection = 0))
      this.$emit('deselected')
    },
    onDelete() {
      this.$emit('delete')
    },
    calculateResizeFactor() {
      // TODO Use function instead of copy psting code
      let markerResizeFactor = 1

      if (this.viewer !== undefined || this.viewer.world !== undefined) {
        const imageSize = this.viewer.world.getItemAt(0).getContentSize()
        const currentImageSizePixelCount = imageSize.x * imageSize.y
        const standard4kPixelCount = 3840 * 2160
        const windowPixelCount = window.innerWidth * window.innerHeight
        let scaleBigResolutions = 1
        let scaleSmallResolutions = 1

        if (windowPixelCount >= 1920 * 1080)
          scaleBigResolutions = Math.pow(windowPixelCount / (1920 * 1080), 0.05)
        else if (windowPixelCount <= 1360 * 720)
          scaleSmallResolutions =
            Math.pow((1920 * 1080) / windowPixelCount, 1.25) / 2.2
        markerResizeFactor =
          Math.cbrt(currentImageSizePixelCount / standard4kPixelCount) *
          scaleSmallResolutions *
          scaleBigResolutions
      }

      return markerResizeFactor
    },
    // Store the initial coordinates and size of all polygons
    storeInitialPolygonsData() {
      const paths = paper.project.activeLayer.children.filter(
        child => child instanceof paper.Path
      )

      this.originalPolygonsData = paths.reduce((acc, path) => {
        acc[path.id] = {
          points: path.segments.map(segment => segment.point),
          bounds: path.bounds,
          position: path.position
        }
        return acc
      }, {})
    },

    // Function to scale the polygons by a centroid-based uniform scale
    scaleAllPaths() {
      const scaleFactor = parseFloat(this.scaleValue)

      // If "Affect all polygons" is not checked, use only the last selected polygon
      if (!this.affectAllPolygons && this.path) {
        this.scaleSinglePath(this.path, scaleFactor)
        return
      }
      if (!this.affectAllPolygons && !this.path) return
      // Calculate the centroid of all polygons
      const paths = paper.project.activeLayer.children.filter(
        child => child instanceof paper.Path
      )
      let centroid = new paper.Point(0, 0)
      let totalArea = 0

      paths.forEach(path => {
        centroid = centroid.add(path.bounds.center)
        totalArea++
      })
      centroid = centroid.divide(totalArea) // Average centroid of all polygons

      // Apply uniform scaling around the centroid
      paths.forEach((path, index) => {
        const initialData = this.originalPolygonsData[path.id]

        // Calculate scaling factor based on the initial width of the path
        const scale =
          scaleFactor * (initialData.bounds.width / path.bounds.width)

        // Scale each path from the centroid
        path.scale(scale, centroid)
      })

      paper.view.draw()
    },
    scaleSinglePath(path, scaleFactor) {
      // Find the initial data based on the path's unique ID
      const initialData = this.originalPolygonsData[path.id]

      if (!initialData) {
        console.error('Initial data not found for the path.')
        return
      }

      const centroid = path.bounds.center

      // Calculate the scaling factor as an absolute transformation (not incremental)
      const scale = scaleFactor * (initialData.bounds.width / path.bounds.width)
      // Apply the scaling transformation on the single path
      path.scale(scale, centroid)

      paper.view.draw()
    },

    // Function to move the polygons based on x and y translation
    moveAllPaths() {
      // this parsing should move to the watch but alas
      let x = parseFloat(this.translation.x)
      let y = parseFloat(this.translation.y)

      if (isNaN(x)) x = 0
      if (isNaN(y)) y = 0

      const paths = paper.project.activeLayer.children.filter(
        child => child instanceof paper.Path
      )

      // Move each path based on the initial position + translation
      paths.forEach((path, index) => {
        const initialData = this.originalPolygonsData[path.id]
        const translationVector = new paper.Point(x, y)

        // Move the polygon by the translation values, based on initial position
        path.position = initialData.position.add(translationVector)
      })

      paper.view.draw()
    }

    // resolve: function(object, key) {
    //   try {
    //     const _keys = key.split('.')
    //     let _cursor = object
    //     for (const _key of _keys) {
    //       _cursor = _cursor[_key]
    //     }
    //     return _cursor
    //   } catch (e) {
    //     return null
    //   }
    // },

    /**
     @see https://openseadragon.github.io/examples/ui-customize-tooltips/
     */
    // translate: function() {
    //   if (!this.i18n || !this.messages) {
    //     return
    //   }
    //
    //   const _messages = this.i18n.messages[this.i18n.locale]
    //   for (const label in this.messages) {
    //     OpenSeadragon.setString(
    //       label,
    //       this.resolve(_messages, this.messages[label])
    //     )
    //   }
    // }
  }
}
</script>
